import { FormBuilder } from '@/components/formBuilder';
import { useInterviewDetailsConfig } from '../formConfigs/useInterviewDetailsConfig';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormikProps, setNestedObjectValues } from 'formik';
import {
  useMeetingLocationConfig,
  useNoteTakerConfig,
  useOfficeConfig,
  useInterviewerPoolConfig,
  useSharedCalendarConfig,
} from '@/formConfigs';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from './AddInterviewActivity.interface';
import { useInterviewStageManager } from '@/pages/ats-job-creation/tabs/interview-plan/stage/stage-manager';

export const InterviewActivityDetails = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, selectedActivity }, ref) => {
      const isHackerRank = selectedActivity === 'hackerrank_interview';
      const {
        interviewerWithVariables,
        interviewType,
        interviewerSelectionOptions,
        interviewers,
      } = useInterviewDetailsConfig();

      const { activityAction } = useInterviewStageManager();
      const { stage = {} } = activityAction as { stage: { name?: string } };

      const noteTakerConfig = useNoteTakerConfig();
      const { formConfig: meetingLocationConfig, defaultLocation } =
        useMeetingLocationConfig({
          isHackerRank,
        });
      const officeConfig = useOfficeConfig();
      const interviewerPoolConfig = useInterviewerPoolConfig();
      const { formConfig: sharedCalendarConfig } = useSharedCalendarConfig({});

      const formConfig = [
        { type: 'sectionLabel', label: 'Interview Details' },
        interviewerWithVariables,
        interviewType,
        interviewerSelectionOptions,
        interviewers,
        interviewerPoolConfig,
        { type: 'sectionLabel', label: 'Schedule details' },
        ...sharedCalendarConfig,
        {
          name: 'duration',
          label: 'Duration',
          type: 'numberStepper',
          step: 15,
          min: 15,
          max: 600,
          unitLabel: 'mins',
        },
        {
          type: 'switch',
          name: 'calendar_visibility',
          label: 'Make the interview private',
          subLabel: 'Hide calendar event details from non-attendees',
        },
        meetingLocationConfig,
        officeConfig,
        {
          type: 'select',
          name: 'host',
          label: 'Host',
          options: [
            { label: 'Organizer', value: 'organizer' },
            {
              label: 'One of the interviewers',
              value: 'one_of_the_interviewers',
            },
          ],
        },
        noteTakerConfig,
      ];

      const initialValues = useMemo(
        () => ({
          interviewName: stage?.name ? `Interview - ${stage?.name}` : '',
          interview_type: 'one_on_one',
          interviewTypeRadio: 'manual_selection',
          participant_ids: [],
          interview_pool_id: '',
          duration: 30,
          schedule_selection: 'manual',
          calendar: 'organizer_calendar',
          calendar_visibility: false,
          office: '',
          host: 'organizer',
          location: defaultLocation,
          enable_note_taker: false,
          ext_interview_template_id: null,
        }),
        [defaultLocation, stage]
      );

      const onSubmit = () => {};
      const formRef = useRef<FormikProps<Record<string, unknown>>>(null);

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const f = formRef.current;
            if (!f) {
              return true;
            }
            const errors = await f.validateForm();
            if (Object.keys(errors).length > 0) {
              f.setTouched(setNestedObjectValues(f.values, true));
              return false;
            }
            return true;
          },
          syncToPayload: () => {
            const f = formRef.current;
            if (f) {
              payloadRef.current.details = { ...f.values };
            }
          },
        }),
        [payloadRef]
      );

      return (
        <Flex justifyContent="center" w="100%" minH={0} flex="1">
          <Flex w="560px" maxW="100%" flexDir="column" pb="6">
            <FormBuilder
              formRef={formRef}
              config={formConfig}
              initialValues={initialValues}
              formContainerProps={{ width: '100%' }}
              onSubmit={onSubmit}
              useGridStyles
            />
          </Flex>
        </Flex>
      );
    }
  )
);

InterviewActivityDetails.displayName = 'InterviewActivityDetails';
